/**
 * OIDC Authentication Utilities
 */

/**
 * Get the access token from session storage or localStorage
 * This is primarily used to check if user is authenticated
 */
export const getAccessToken = (): string | null => {
    // In this implementation, we rely on server-side session cookies
    // So we don't store tokens in the browser
    // Instead, we check if the user has a valid session
    return null;
};

/**
 * Check if user is authenticated by checking session
 */
export const isAuthenticated = async (): Promise<boolean> => {
    try {
        const response = await fetch('/rest/Profile', {
            credentials: 'include',
        });
        return response.ok;
    } catch {
        return false;
    }
};

/**
 * Redirect to OIDC login
 */
export const redirectToLogin = () => {
    const currentPath = window.location.pathname + window.location.search;
    window.location.href = `/oidc?redirect=${encodeURIComponent(currentPath)}`;
};

/**
 * Logout and redirect to OIDC logout
 */
export const logout = async () => {
    try {
        const sessionResponse = await fetch('/rest/Authenticate/session', { credentials: 'include', cache: 'no-store' });
        if (!sessionResponse.ok) throw new Error('Could not check sign-in session.');
        const session = await sessionResponse.json();
        const response = await fetch('/rest/Authenticate/logout', {
            method: 'POST',
            headers: { 'X-CSRF-Token': session.csrf },
            credentials: 'include',
        });
        const data = await response.json();

        if (data.logoutUrl) {
            window.location.href = data.logoutUrl;
        } else {
            window.location.href = '/';
        }
    } catch {
        window.location.href = '/';
    }
};

export type SilentOidcResult =
    | { status: 'success' }
    | { status: 'error'; reason?: string; description?: string }
    | { status: 'timeout' };

/**
 * Perform a silent OIDC auth roundtrip (prompt=none) in a hidden iframe.
 *
 * This attempts to reuse the IdP session. Cached provider attributes can still be stale;
 * use interactive sign-in when fresh authentication is required.
 *
 * Requirements:
 * - `oidc.php` must postMessage({ type: "oidc_silent", status: "success"|"error" }) to the parent.
 */
export const silentOidcRefresh = async (options?: {
    timeoutMs?: number;
    redirect?: string;
}): Promise<SilentOidcResult> => {
    const timeoutMs = options?.timeoutMs ?? 15000;
    const redirect = options?.redirect ?? '/';

    const origin = window.location.origin;

    return await new Promise<SilentOidcResult>((resolve) => {
        let done = false;
        const finish = (result: SilentOidcResult) => {
            if (done) return;
            done = true;
            cleanup();
            resolve(result);
        };

        let iframe: HTMLIFrameElement | null = document.getElementById(
            'oidc-silent-iframe'
        ) as HTMLIFrameElement | null;

        const onMessage = (event: MessageEvent) => {
            if (event.origin !== origin || event.source !== iframe?.contentWindow) return;
            const data = event.data as any;
            if (!data || data.type !== 'oidc_silent') return;

            if (data.status === 'success') finish({ status: 'success' });
            else
                finish({
                    status: 'error',
                    reason: String(data.reason ?? '') || undefined,
                    description: String(data.description ?? '') || undefined,
                });
        };

        const cleanup = () => {
            window.removeEventListener('message', onMessage);
            window.clearTimeout(timeout);
            try {
                if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
            } catch {
                // ignore
            }
            iframe = null;
        };

        window.addEventListener('message', onMessage);

        const timeout = window.setTimeout(() => {
            finish({ status: 'timeout' });
        }, timeoutMs);

        const url = `/oidc?silent=1&prompt=none&redirect=${encodeURIComponent(redirect)}&_=${Date.now()}`;

        if (!iframe) {
            iframe = document.createElement('iframe');
            iframe.id = 'oidc-silent-iframe';
            iframe.style.display = 'none';
            iframe.src = url;
            document.body.appendChild(iframe);
        } else {
            // Force reload for subsequent refresh attempts
            iframe.src = url;
        }
    });
};
